import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';

import {
  getZipSubmissionPushReadiness,
  pushZipSubmissionToFacebook,
} from '@src/api/actions/dashboard/zipSubmissions';

// Stages:
//   'loading'  — fetching readiness
//   'ready'    — readiness rendered, waiting for user action
//   'pushing'  — POST in flight
//   'result'   — push complete (may include partial failures)
//   'error'    — fatal load error

const CHECK_LABELS = {
  pushable_zips_exist: {
    pass: 'ZIPs available to push',
    fail: 'No pushable ZIPs',
    help: 'No territories for this vertical have budget this month. Add a budget to push.',
  },
  submission_territory_has_budget: {
    pass: 'Submission territory has budget',
    fail: 'Submission territory has no budget',
    help: 'This submission\'s territory has no budget this month. Other territories may still push.',
    nonBlocking: true,
  },
  broad_campaigns_found: {
    pass: 'Broad campaigns found',
    fail: 'No broad campaigns found',
    help: 'No ct=broad campaigns under an active ad account match this vertical.',
  },
};

function PreflightCheck({ name, ok }) {
  const label = CHECK_LABELS[name] || {};
  const Icon = ok ? CheckCircleIcon : CancelIcon;
  const color = ok ? 'success.main' : label.nonBlocking ? 'warning.main' : 'error.main';
  return (
    <Stack direction="row" spacing={1} alignItems="flex-start">
      <Icon sx={{ color, mt: '2px' }} fontSize="small" />
      <Box>
        <Typography variant="body2" fontWeight={500}>
          {ok ? label.pass : label.fail}
        </Typography>
        {!ok && label.help && (
          <Typography variant="caption" color="text.secondary">
            {label.help}
          </Typography>
        )}
      </Box>
    </Stack>
  );
}

function TerritoryTable({ rows }) {
  return (
    <TableContainer sx={{ maxHeight: 240 }}>
      <Table size="small" stickyHeader>
        <TableHead>
          <TableRow>
            <TableCell>Territory</TableCell>
            <TableCell align="right">ZIPs</TableCell>
            <TableCell>Status</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map((row) => (
            <TableRow key={row.id ?? row.name}>
              <TableCell>{row.name}</TableCell>
              <TableCell align="right">{row.count}</TableCell>
              <TableCell>
                {row.included ? (
                  <Chip size="small" color="success" label="Included" variant="outlined" />
                ) : (
                  <Chip
                    size="small"
                    color="default"
                    label="No budget this month"
                    variant="outlined"
                  />
                )}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
}

function FbTargets({ adAccounts }) {
  const [openAccounts, setOpenAccounts] = useState(() => new Set(adAccounts.map((a) => a.id)));
  const toggle = (id) => {
    setOpenAccounts((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  return (
    <Stack spacing={1}>
      {adAccounts.map((acct) => {
        const totalAdSets = acct.campaigns.reduce((sum, c) => sum + c.ad_sets.length, 0);
        const isOpen = openAccounts.has(acct.id);
        return (
          <Box key={acct.id} sx={{ border: 1, borderColor: 'divider', borderRadius: 1 }}>
            <Stack
              direction="row"
              alignItems="center"
              justifyContent="space-between"
              sx={{ px: 1.5, py: 1, cursor: 'pointer' }}
              onClick={() => toggle(acct.id)}
            >
              <Box>
                <Typography variant="body2" fontWeight={600}>
                  {acct.name || acct.account_id}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {acct.campaigns.length} broad campaign{acct.campaigns.length !== 1 ? 's' : ''} · {totalAdSets} ad set{totalAdSets !== 1 ? 's' : ''}
                </Typography>
              </Box>
              <IconButton size="small">
                {isOpen ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
              </IconButton>
            </Stack>
            <Collapse in={isOpen}>
              <Box sx={{ px: 2, pb: 1 }}>
                {acct.campaigns.map((camp) => (
                  <Box key={camp.id} sx={{ mb: 1 }}>
                    <Typography variant="caption" color="text.secondary">
                      {camp.name}
                    </Typography>
                    <Stack direction="row" spacing={0.5} flexWrap="wrap" mt={0.5}>
                      {camp.ad_sets.map((a) => (
                        <Chip
                          key={a.id}
                          size="small"
                          label={a.name || a.ad_set_id}
                          variant="outlined"
                        />
                      ))}
                    </Stack>
                  </Box>
                ))}
              </Box>
            </Collapse>
          </Box>
        );
      })}
    </Stack>
  );
}

function ResultList({ result, onRetry, retrying }) {
  const failed = result.results.filter((r) => !r.success);
  const succeeded = result.results.filter((r) => r.success);
  return (
    <Stack spacing={1.5}>
      <Alert severity={failed.length === 0 ? 'success' : 'warning'}>
        Pushed to {succeeded.length} ad set{succeeded.length !== 1 ? 's' : ''}
        {failed.length > 0 && ` · ${failed.length} failed`}
        {' · '}
        {result.zip_count} ZIPs
      </Alert>
      {failed.length > 0 && (
        <Box>
          <Typography variant="subtitle2" gutterBottom>
            Failed ad sets
          </Typography>
          <Stack spacing={0.5}>
            {failed.map((r) => (
              <Alert
                key={r.ad_set_id}
                severity="error"
                icon={<ErrorOutlineIcon />}
                variant="outlined"
                sx={{ py: 0 }}
              >
                <Typography variant="body2" fontWeight={500}>
                  {r.ad_set_name || r.ad_set_id}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {r.error}
                </Typography>
              </Alert>
            ))}
          </Stack>
          <Button
            sx={{ mt: 1 }}
            size="small"
            variant="outlined"
            onClick={() => onRetry(failed.map((r) => r.ad_set_id))}
            disabled={retrying}
          >
            {retrying ? <CircularProgress size={16} /> : `Retry failed (${failed.length})`}
          </Button>
        </Box>
      )}
    </Stack>
  );
}

export default function PushToFacebookModal({
  open,
  onClose,
  submissionId,
  onPushComplete,
}) {
  const [stage, setStage] = useState('loading');
  const [readiness, setReadiness] = useState(null);
  const [error, setError] = useState('');
  const [result, setResult] = useState(null);
  const [retrying, setRetrying] = useState(false);

  const loadReadiness = useCallback(async () => {
    if (!submissionId) return;
    setStage('loading');
    setError('');
    try {
      const res = await getZipSubmissionPushReadiness(submissionId);
      setReadiness(res.data);
      setStage('ready');
    } catch (err) {
      const detail = err?.response?.data?.detail || err?.message || 'Failed to load push readiness.';
      setError(detail);
      setStage('error');
    }
  }, [submissionId]);

  useEffect(() => {
    if (open && submissionId) {
      setResult(null);
      loadReadiness();
    }
  }, [open, submissionId, loadReadiness]);

  const handlePush = async (retryIds = null) => {
    const payload = retryIds ? { retry_ad_set_ids: retryIds } : {};
    if (retryIds) setRetrying(true);
    else setStage('pushing');
    try {
      const res = await pushZipSubmissionToFacebook(submissionId, payload);
      const newResult = res.data;
      if (retryIds && result) {
        const priorSuccess = result.results.filter((r) => r.success);
        const merged = {
          ...newResult,
          results: [...priorSuccess, ...newResult.results],
          pushed_count: priorSuccess.length + newResult.pushed_count,
          failed_count: newResult.failed_count,
        };
        setResult(merged);
      } else {
        setResult(newResult);
      }
      setStage('result');
      onPushComplete?.();
    } catch (err) {
      const status = err?.response?.status;
      if (status === 409) {
        // Readiness went stale — reload and stay in "ready" stage so user sees the new state.
        const payload = err?.response?.data || {};
        if (payload.readiness) {
          setReadiness(payload.readiness);
          setError(
            payload.code === 'no_pushable_zips'
              ? 'State changed: no pushable ZIPs remain. Refresh and try again.'
              : 'State changed while modal was open. Updated readiness below.',
          );
          setStage('ready');
        } else {
          await loadReadiness();
        }
      } else {
        const detail = err?.response?.data?.detail || err?.message || 'Push failed.';
        setError(detail);
        setStage('ready');
      }
    } finally {
      setRetrying(false);
    }
  };

  const canPush = stage === 'ready' && readiness?.can_push;
  const submission = readiness?.submission;

  const title = useMemo(() => {
    if (!submission) return 'Push ZIPs to Facebook';
    const bits = [];
    if (submission.vertical) bits.push(submission.vertical);
    if (submission.territory) bits.push(submission.territory);
    return `Push to Facebook — ${bits.join(' · ')}`;
  }, [submission]);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>{title}</DialogTitle>
      <DialogContent dividers>
        {stage === 'loading' && (
          <Box display="flex" justifyContent="center" py={4}>
            <CircularProgress />
          </Box>
        )}

        {stage === 'error' && (
          <Alert severity="error" sx={{ my: 1 }}>
            {error}
          </Alert>
        )}

        {(stage === 'ready' || stage === 'pushing' || stage === 'result') && readiness && (
          <Stack spacing={2.5}>
            {error && stage === 'ready' && (
              <Alert severity="warning">{error}</Alert>
            )}

            <Box>
              <Typography variant="subtitle2" gutterBottom>
                Pre-flight
              </Typography>
              <Stack spacing={1}>
                <PreflightCheck
                  name="pushable_zips_exist"
                  ok={readiness.checks.pushable_zips_exist}
                />
                <PreflightCheck
                  name="submission_territory_has_budget"
                  ok={readiness.checks.submission_territory_has_budget}
                />
                <PreflightCheck
                  name="broad_campaigns_found"
                  ok={readiness.checks.broad_campaigns_found}
                />
              </Stack>
            </Box>

            <Divider />

            <Box>
              <Stack direction="row" justifyContent="space-between" alignItems="baseline" mb={1}>
                <Typography variant="subtitle2">ZIPs to push</Typography>
                <Typography variant="body2" color="text.secondary">
                  <strong>{readiness.zip_set.total_pushable}</strong> pushable
                  {readiness.zip_set.filtered_out_count > 0 && (
                    <> · {readiness.zip_set.filtered_out_count} excluded by targeting filters</>
                  )}
                </Typography>
              </Stack>
              <TerritoryTable rows={readiness.zip_set.by_territory} />
            </Box>

            {readiness.fb_targets.ad_accounts.length > 0 && (
              <>
                <Divider />
                <Box>
                  <Typography variant="subtitle2" gutterBottom>
                    Will replace geo_locations on
                  </Typography>
                  <FbTargets adAccounts={readiness.fb_targets.ad_accounts} />
                </Box>
              </>
            )}

            {stage === 'result' && result && (
              <>
                <Divider />
                <ResultList
                  result={result}
                  onRetry={(ids) => handlePush(ids)}
                  retrying={retrying}
                />
              </>
            )}
          </Stack>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{stage === 'result' ? 'Close' : 'Cancel'}</Button>
        {stage !== 'result' && (
          <Button
            variant="contained"
            color="primary"
            onClick={() => handlePush()}
            disabled={!canPush || stage === 'pushing'}
          >
            {stage === 'pushing' ? <CircularProgress size={18} /> : 'Push to Facebook'}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}
